import React from 'react';

import { InfoImage } from '@infinex-private/connect-web-shared/components';
import { ArrowUpIcon, ArrowDownIcon, CheckIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { Text } from '@infinex/ui/components';

import type {
  NormalizedAssetDiff,
  NormalizedAssetApproval,
} from '../ValidationResultCard/normalizeChainResponse';

interface EstimatedWalletChangesRowProps {
  item: NormalizedAssetDiff | NormalizedAssetApproval;
  type: 'diff' | 'approval';
}

export function EstimatedWalletChangesRow({
  item,
  type,
}: EstimatedWalletChangesRowProps) {
  const isApproval = type === 'approval';
  const isDiff = type === 'diff';
  const isReceiving = isDiff && (item as NormalizedAssetDiff).isReceiving;

  // Use the pre-formatted amount from normalization
  const formattedAmount = item.amount;

  // Get the appropriate icon and color based on the action type
  const getActionStyle = () => {
    if (isApproval) {
      return {
        bgColor: 'bg-fillHighlight',
        iconColor: 'fill-highlight',
        textColor: 'text-emphasis',
        icon: CheckIcon,
        label: 'Approving',
        prefix: '',
      };
    }
    if (isReceiving) {
      return {
        bgColor: 'bg-fillPositive',
        iconColor: 'fill-positive',
        textColor: 'text-positive',
        icon: ArrowDownIcon,
        label: 'Receiving',
        prefix: '+',
      };
    }
    return {
      bgColor: 'bg-fillCritical',
      iconColor: 'fill-critical',
      textColor: 'text-critical',
      icon: ArrowUpIcon,
      label: 'Sending',
      prefix: '-',
    };
  };

  const actionStyle = getActionStyle();
  const Icon = actionStyle.icon;

  return (
    <div className="flex items-center gap-3">
      {/* Icon circle */}
      <div
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded-full',
          actionStyle.bgColor
        )}
      >
        <Icon
          className={cn(actionStyle.iconColor, 'rounded-full')}
          size="2xsmall"
        />
      </div>

      {/* Label */}
      <Text className="text-emphasis text-sm font-medium">
        {actionStyle.label}
      </Text>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Asset info */}
      <div className="flex min-w-0 items-center gap-2">
        <InfoImage
          src={item.asset.logoUrl || ''}
          alt={item.asset.name}
          size={20}
          className="shrink-0 rounded-full"
        />
        <Text
          className={cn(
            'min-w-0 text-right text-base font-medium break-all',
            actionStyle.textColor
          )}
        >
          {item.amount === 'Unlimited' ? '' : actionStyle.prefix}
          {formattedAmount} {item.asset.symbol}
        </Text>
      </div>
    </div>
  );
}
